import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
    Button,
    CircularProgress,
    Grid,
    LinearProgress,
    Typography,
} from '@mui/material';
import { DataGrid, GridOverlay } from '@mui/x-data-grid';
//import rowData from '../Sample_TrainingData.json';
import moment from 'moment-timezone';
import TranscriptionData from './TranscriptionData';
import OrderSummaryDisplay from './OrderSummaryDisplay';
import SelectWithLabel from '../../../components/common/SelectWithLabel';
import AiTrainingDataContext from '../../AiTrainingDataContext';
import DialogWithContent from '../../../components/common/DialogWithContent/DialogWithContent';
import TriggerSimulationJob from './TriggerSimulationJob';

const useStyles = makeStyles((theme) => ({
    root: {
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            padding: 0,
            margin: 0,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            overflow: 'none',
        },
        '& .MuiDataGrid-iconButtonContainer': {
            padding: 0,
            margin: 0,
        },
        '& .MuiIconButton-root': {
            padding: 0,
            margin: 0,
        },
    },
}));

function CustomLoadingOverlay() {
    return (
        <GridOverlay>
            <div style={{ position: 'absolute', top: 0, width: '100%' }}>
                <LinearProgress />
            </div>
        </GridOverlay>
    );
}

function AiTrainingDataResults(props) {
    const { dispatchMessageToDisplay, appliedFiltersData } = props;

    const [jobId, setJobId] = useState('');
    //const [promptToUse, setPromptToUse] = useState('');
    const [promptVersionIdToUse, setPromptVersionIdToUse] = useState('');
    const [selectedRecordings, setSelectedRecordings] = React.useState([]);

    const {
        statusListItems,
        loading,
        setLoading,
        callRecAndTrainingData,
        setCallRecAndTrainingData,
        restauTimeZone,
        saveCallRecordingData,
        reloadCallRecordingData,
        saveAndTriggerSimulationJob,
    } = useContext(AiTrainingDataContext);

    const classes = useStyles();
    const [pageSize, setPageSize] = useState(5);
    const [openSimulationJobDetails, setOpenSimulationJobDetails] =
        useState(false);

    const rowHeight = 120;

    const columns = [
        {
            field: 'callStartTime',
            type: 'number',
            description: '',
            headerName: 'Call Start Time',
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                const callStartTime = params.row.callStartTime;
                if (!callStartTime) return 'Invalid Date';
                const callStartTimeObj = moment.tz(
                    callStartTime,
                    'MM/DD/YYYY hh:mm:ss A',
                    restauTimeZone
                );
                const date = callStartTimeObj.format('MMDDYYYY');
                const time = callStartTimeObj.format('hh:mm:ss A');

                return (
                    <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>
                        {date}
                        <br />
                        {time}
                    </div>
                );
            },
        },
        {
            field: 'recordingUrl',
            // type: 'number',
            headerName: 'Call Recording',
            flex: 1.25,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <audio controls style={{ width: '100%' }}>
                    <source src={params.row.recordingUrl} type="audio/mpeg" />
                    Your browser does not support the audio element.
                </audio>
            ),
        },
        {
            field: 'parsedTranscriptionByAI',
            description: '',
            // type: 'text',
            flex: 1.75,
            headerName: 'Transcription',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <TranscriptionData
                    rowId={params.row.id}
                    transcriptionData={
                        params.row.parsedTranscriptionByCrowd ||
                        params.row.parsedTranscriptionByAI
                    }
                    updateTranscriptionTextForRow={
                        updateTranscriptionTextForRow
                    }
                    allowEdits={true}
                />
            ),
        },
        {
            field: 'orderSummary',
            headerName: 'Order Summary',
            flex: 1.25,
            // type: 'number',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <OrderSummaryDisplay
                    rowId={params.row.id}
                    itemsInOrder={
                        params.row.orderSummaryByCrowd ||
                        params.row.orderSummaryByAI
                    }
                    rowHeight={rowHeight}
                    updateOrderSummaryForRow={updateOrderSummaryForRow}
                    removeItemFromOrderSummaryForRow={
                        removeItemFromOrderSummaryForRow
                    }
                    updateTranscriptionTextForRow={
                        updateTranscriptionTextForRow
                    }
                />
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            type: 'number',
            flex: 0.75,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <SelectWithLabel
                    label={'Status'}
                    items={statusListItems}
                    value={params.row.status}
                    onChangeHandler={(newValue) => {
                        // Handle status change logic here
                        console.log(`Status changed to: ${newValue}`);
                        updateStatusForRow(
                            params.row.id,
                            newValue ? newValue : 'inProgress'
                        );
                    }}
                />
            ),
        },
        {
            field: 'save',
            headerName: 'Save',
            // type: 'number',
            flex: 0.35,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!params.row.edited || params.row.saveInProgress}
                    //disabled={!params.row.saveInProgress}
                    onClick={() => saveOrderSummaryForRow(params.row.id)}
                >
                    {params.row.saveInProgress ? (
                        <CircularProgress
                            size={24}
                            style={{ color: 'black' }}
                        />
                    ) : (
                        'Save'
                    )}
                </Button>
            ),
        },
    ];

    const updateOrderSummaryForRow = (rowId, newItems) => {
        //alert(`${rowId} - ${newItems}`);
        setCallRecAndTrainingData((prevData) =>
            prevData.map((row) => {
                if (row.id === rowId) {
                    let temp = row.orderSummaryByCrowd || row.orderSummaryByAI;
                    row.edited = true;
                    return {
                        ...row,
                        orderSummaryByCrowd: [...temp, ...newItems].filter(
                            (item, index, self) =>
                                index ===
                                self.findIndex((t) => t.key === item.key)
                        ),
                    };
                }
                return row;
            })
        );
    };

    const updateStatusForRow = (rowId, updatedStatus) => {
        //alert(`${rowId} - ${newItems}`);
        setCallRecAndTrainingData((prevData) =>
            prevData.map((row) => {
                if (row.id === rowId) {
                    row.edited = true;
                    return {
                        ...row,
                        status: updatedStatus,
                    };
                }
                return row;
            })
        );
    };

    const removeItemFromOrderSummaryForRow = (rowId, itemKey) => {
        //debugger;
        setCallRecAndTrainingData((prevData) =>
            prevData.map((row) => {
                if (row.id === rowId) {
                    row.edited = true;
                    //if orderSummaryByCrowd is not defined, create it
                    if (row.orderSummaryByCrowd) {
                        row.orderSummaryByCrowd = JSON.parse(
                            JSON.stringify(row.orderSummaryByCrowd)
                        );
                    }
                    return {
                        ...row,
                        orderSummaryByCrowd: row.orderSummaryByCrowd.filter(
                            (item) => item.key !== itemKey
                        ),
                    };
                }
                return row;
            })
        );
    };

    const saveOrderSummaryForRow = async (rowId) => {
        try {
            const rowData = callRecAndTrainingData.find(
                (row) => row.id === rowId
            );
            //set loading status
            setCallRecAndTrainingData((prevData) =>
                prevData.map((row) => {
                    if (row.id === rowId) {
                        return {
                            ...row,
                            saveInProgress: true,
                        };
                    }
                    return row;
                })
            );
            let res = await saveCallRecordingData(
                rowId,
                rowData.orderSummaryByCrowd,
                rowData.parsedTranscriptionByCrowd,
                rowData.status
            );
            if (res) {
                dispatchMessageToDisplay({
                    type: 'SHOW_SUCCESS_MESSAGE',
                    payload: {
                        showMessage: true,
                        message: 'Order summary saved successfully!',
                        isSuccessMessage: true,
                    },
                });
            } else {
                dispatchMessageToDisplay({
                    type: 'SHOW_ERROR_MESSAGE',
                    payload: {
                        showMessage: true,
                        message: 'Failed to save order summary!',
                        isSuccessMessage: false,
                    },
                });
            }
            setLoading(true);
            await reloadCallRecordingData(appliedFiltersData);
            setLoading(false);
        } catch (error) {
            dispatchMessageToDisplay({
                type: 'SHOW_ERROR_MESSAGE',
                payload: {
                    showMessage: true,
                    message: `Failed to save order summary. ${error.message}`,
                    isSuccessMessage: false,
                },
            });
            return false;
        }
    };

    const updateTranscriptionTextForRow = (rowId, transcriptionData) => {
        //debugger;
        //add code here
        setCallRecAndTrainingData((prevData) =>
            prevData.map((row) => {
                if (row.id === rowId) {
                    row.edited = true;
                    return {
                        ...row,
                        parsedTranscriptionByCrowd: transcriptionData,
                    };
                }
                return row;
            })
        );
    };

    const validateAndSaveSimulationJob = async () => {
        try {
            //debugger;
            //do some basic validations
            if (!jobId || !promptVersionIdToUse) {
                throw new Error('Input parameters are missing !');
            }

            // const majorVersion = parseInt(promptVersionToUse.split('.')[0]);
            // const minorVersion = parseInt(promptVersionToUse.split('.')[1]);

            // if (isNaN(majorVersion) || isNaN(minorVersion)) {
            //     throw new Error('Invalid prompt version selected !');
            // }

            //setLoading(true);
            await saveAndTriggerSimulationJob(
                jobId,
                promptVersionIdToUse,
                selectedRecordings
            );
            dispatchMessageToDisplay({
                type: 'SHOW_SUCCESS_MESSAGE',
                payload: {
                    showMessage: true,
                    message: `Simulation Job triggered succesfully...`,
                    isSuccessMessage: true,
                },
            });
            //setLoading(false);
        } catch (error) {
            setLoading(false);
            dispatchMessageToDisplay({
                type: 'SHOW_ERROR_MESSAGE',
                payload: {
                    showMessage: true,
                    message: `Failed to Trigger Simulation Job. ${error.message}`,
                    isSuccessMessage: false,
                },
            });
            console.error('Failed to Trigger Simulation Job:', error);
        }
    };

    const openRunSimulationPopup = async () => {
        if (!selectedRecordings || selectedRecordings.length === 0) {
            alert('Please select atleast one recording...');
            return;
        }

        setOpenSimulationJobDetails(true);
    };

    useEffect(() => {
        // Fetch data and set loading state
        setCallRecAndTrainingData([]);
    }, []);

    return (
        <>
            <DialogWithContent
                dialogTitle="Simulation Job"
                dialogMaxWidth="md"
                setOpen={setOpenSimulationJobDetails}
                open={openSimulationJobDetails}
                handleSave={validateAndSaveSimulationJob}
                dialogSaveBtnText={'Save and Trigger'}
            >
                <TriggerSimulationJob
                    jobId={jobId}
                    setJobId={setJobId}
                    promptVersionIdToUse={promptVersionIdToUse}
                    setPromptVersionIdToUse={setPromptVersionIdToUse}
                    selectedRecordings={selectedRecordings}
                />
            </DialogWithContent>
            <Grid container item>
                <Grid container item alignContent={'center'}>
                    <Grid item xs={12} sm={1}>
                        <Typography variant="h5" sx={{ mb: 0 }}>
                            Results
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Button
                            color="primary"
                            style={{
                                height: 'max-content',
                                marginRight: '10px',
                            }}
                            variant="contained"
                            onClick={openRunSimulationPopup}
                        >
                            Run Simulation
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <DataGrid
                        className={classes.root}
                        rows={callRecAndTrainingData}
                        columns={columns}
                        checkboxSelection
                        onSelectionModelChange={(ids) => {
                            setSelectedRecordings(ids);
                        }}
                        autoHeight={true}
                        loading={loading}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) =>
                            setPageSize(newPageSize)
                        }
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        pagination
                        components={{
                            LoadingOverlay: CustomLoadingOverlay,
                        }}
                        rowHeight={rowHeight}
                    />
                </Grid>
            </Grid>
        </>
    );
}

AiTrainingDataResults.propTypes = {
    dispatchMessageToDisplay: PropTypes.func.isRequired,
    appliedFiltersData: PropTypes.object.isRequired,
};

export default AiTrainingDataResults;
